import type { BrandVariants } from '@fluentui/react-components';

import { brand } from './brand';
import { AA_CONTRAST, contrastRatio, generateRamp, normaliseHex, type BrandStop } from './ramp';

/**
 * The palettes a user can choose from, and the rule that decides which ramp
 * stop becomes the brand-filled surface (the header, primary buttons, the
 * active page number).
 *
 * THE CONTRAST POLICY, in one paragraph. Fluent puts white
 * (`colorNeutralForegroundOnBrand`) on every brand-filled surface, so the stop
 * chosen for that surface decides whether every primary action in the app
 * passes WCAG AA. Orange is a deliberate, documented exception — stop 120 is the
 * real Bedrock orange and white on it is only 2.15:1, accepted for brand
 * fidelity (see `themes.ts` and the README). Every *other* palette, preset or
 * user-chosen, has no brand-fidelity claim on it, so it takes the vividest stop
 * that still clears 4.5:1.
 */

const WHITE = '#FFFFFF';

/**
 * Fluent's own mapping: `createLightTheme` puts `colorBrandBackground` at stop
 * 80. A palette that resolves here needs no surface override at all.
 */
export const DEFAULT_SURFACE_STOP: BrandStop = 80;

/** The one stop the Bedrock orange is pinned to. See the policy note above. */
const ORANGE_SURFACE_STOP: BrandStop = 120;

/**
 * Searched vividest-first. In practice a well-formed ramp always lands on 80:
 * the reference lightness curve puts that stop at L* 44.9, and white against
 * *any* hue at that lightness measures about 5.4:1. This is written as a search
 * rather than a constant so the policy is executable and testable — and so a
 * pathological ramp (a near-white key colour, say) still degrades to something
 * readable instead of silently shipping 2:1 text.
 */
const SURFACE_CANDIDATES: readonly BrandStop[] = [120, 110, 100, 90, 80, 70];

export type PaletteId =
  'orange' | 'blue' | 'teal' | 'green' | 'purple' | 'magenta' | 'red' | 'steel';

export interface Palette {
  id: PaletteId;
  name: string;
  /**
   * The colour the ramp is built around. It appears verbatim at one stop; that
   * is not necessarily the stop used for the filled surface, which is why the
   * settings page shows the resolved surface colour rather than this.
   */
  keyColor: string;
}

/**
 * Orange first, and it is special: `resolveRamp` returns the hand-authored ramp
 * from `brand.ts` rather than generating one, so the shipped brand identity is
 * unchanged to the byte. Its `keyColor` is recorded only so the swatch and the
 * colour picker have something to seed from.
 *
 * The rest are ordinary key colours run through `generateRamp`. They were
 * picked for hue separation — someone glancing at eight swatches should be able
 * to tell them apart — and each is verified against the contrast policy in
 * `palettes.test.ts`.
 */
export const PRESETS: readonly Palette[] = [
  { id: 'orange', name: 'Bedrock orange', keyColor: '#FD991D' },
  { id: 'blue', name: 'Blue', keyColor: '#0F6CBD' },
  { id: 'teal', name: 'Teal', keyColor: '#00807A' },
  { id: 'green', name: 'Green', keyColor: '#0E700E' },
  { id: 'purple', name: 'Purple', keyColor: '#7A4FCF' },
  { id: 'magenta', name: 'Magenta', keyColor: '#B4009E' },
  { id: 'red', name: 'Red', keyColor: '#C4314B' },
  { id: 'steel', name: 'Steel', keyColor: '#4A5C6B' },
];

export type PaletteSelection = { kind: 'preset'; id: PaletteId } | { kind: 'custom'; hex: string };

export const DEFAULT_PALETTE: PaletteSelection = { kind: 'preset', id: 'orange' };

export function isPaletteId(value: unknown): value is PaletteId {
  return PRESETS.some((preset) => preset.id === value);
}

export function findPreset(id: PaletteId): Palette {
  const preset = PRESETS.find((candidate) => candidate.id === id);

  // Unreachable while PaletteId and PRESETS agree, but PRESETS is data and the
  // type is a claim about it; better a clear throw than an undefined ramp.
  if (!preset) throw new Error(`Unknown palette "${id}".`);

  return preset;
}

/** The colour a selection is built around — the swatch, and the picker's seed. */
export function paletteKeyColor(selection: PaletteSelection): string {
  return selection.kind === 'preset' ? findPreset(selection.id).keyColor : selection.hex;
}

function pickSurfaceStop(ramp: BrandVariants): BrandStop {
  return (
    SURFACE_CANDIDATES.find((stop) => contrastRatio(WHITE, ramp[stop]) >= AA_CONTRAST) ??
    DEFAULT_SURFACE_STOP
  );
}

export interface ResolvedPalette {
  ramp: BrandVariants;
  /** Which stop `colorBrandBackground` is pointed at. */
  surfaceStop: BrandStop;
  /** The resolved brand-filled surface colour, for previews and readouts. */
  surface: string;
  /** White-on-surface. Below 4.5 only for orange, by policy. */
  surfaceContrast: number;
}

// Generating a ramp is 16 stops x a 24-step binary search — cheap, but it would
// otherwise run on every render of every consumer.
//
// Bounded because the settings page's colour picker calls this on every frame of
// a drag, and each frame is a different hex: an unbounded map would grow without
// limit for as long as someone plays with it. Oldest-first eviction, which keeps
// the eight presets resident under any realistic use.
const CACHE_LIMIT = 64;
const cache = new Map<string, ResolvedPalette>();

function cacheKey(selection: PaletteSelection): string {
  return selection.kind === 'preset' ? `preset:${selection.id}` : `custom:${selection.hex}`;
}

/**
 * Everything the theme layer and the settings UI need to know about a choice.
 *
 * An unparseable custom hex falls back to the default palette rather than
 * throwing: this runs during render, and a bad value in localStorage should not
 * white-screen the app.
 */
export function resolvePalette(selection: PaletteSelection): ResolvedPalette {
  const key = cacheKey(selection);
  const cached = cache.get(key);
  if (cached) return cached;

  let ramp: BrandVariants;
  let surfaceStop: BrandStop;

  if (selection.kind === 'preset' && selection.id === 'orange') {
    ramp = brand;
    surfaceStop = ORANGE_SURFACE_STOP;
  } else {
    const hex = normaliseHex(paletteKeyColor(selection));
    if (!hex) return resolvePalette(DEFAULT_PALETTE);

    ramp = generateRamp(hex);
    surfaceStop = pickSurfaceStop(ramp);
  }

  const surface = ramp[surfaceStop];
  const resolved: ResolvedPalette = {
    ramp,
    surfaceStop,
    surface,
    surfaceContrast: contrastRatio(WHITE, surface),
  };

  if (cache.size >= CACHE_LIMIT) {
    const oldest = cache.keys().next();
    if (!oldest.done) cache.delete(oldest.value);
  }

  cache.set(key, resolved);
  return resolved;
}
